'use client';

import { logger } from '@adornis/base/logging.js';
import { subscribeCurrentUser } from '@adornis/users/client/currentUser.js';
import { AdornisUser, registerUser } from '@adornis/users/db/a-user.js';
import { useState } from 'react';
import { useTranslation } from 'react-i18next';
import { createStudentMutation } from '../../../../db/api/create-student.js';
import { createTeacherMutation } from '../../../../db/api/create-teacher.js';
import { type Subject, ALL_SUBJECTS } from '../../../../db/subjects.js';
import { router } from '../../../App.js';
import OwlLogo from '../../../assets/OwlTransparent.svg';
import LabeledInput, { MultiSelectDropdown, validateInput } from '../../../components/ui/labeled-inputs.js';
import { getGuestUserID } from '../../../lib/guest-user-id.js';

export default function SignUpPage() {
  const { t } = useTranslation();

  const [showValidation, setShowValidation] = useState(false);
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [signUpError, setSignUpError] = useState<string | null>(null);

  const requiredFields = {
    firstName: true,
    lastName: true,
    school: true,
    subjects: true,
    email: true,
    password: true,
    confirmPassword: true,
  };

  const [formData, setFormData] = useState({
    role: 'teacher' as 'teacher' | 'student',
    firstName: '',
    lastName: '',
    school: '',
    subjects: [] as Subject[],
    email: '',
    password: '',
    confirmPassword: '',
  });

  const hasValidationErrors = () => {
    return (
      (requiredFields.firstName && !validateInput(formData.firstName, 'text')) ||
      (requiredFields.lastName && !validateInput(formData.lastName, 'text')) ||
      (requiredFields.school && !validateInput(formData.school, 'text')) ||
      // Students aren't asked to pick subjects at signup — they add them freely later in their
      // profile, since they aren't restricted to a fixed subject list the way teachers are.
      (formData.role === 'teacher' && requiredFields.subjects && !validateInput(formData.subjects, 'multiselect')) ||
      (requiredFields.email && !validateInput(formData.email, 'email')) ||
      (requiredFields.password && !validateInput(formData.password, 'password')) ||
      (requiredFields.confirmPassword && !validateInput(formData.confirmPassword, 'password'))
    );
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    setShowValidation(true);
    setSignUpError(null);

    if (hasValidationErrors()) return;

    if (formData.password !== formData.confirmPassword) {
      logger.error(`Passwords do not match`);
      setSignUpError('Passwords do not match');
      return;
    }

    setIsSubmitting(true);
    try {
      const username = formData.email;
      const registeredUser = await registerUser({
        username,
        email: formData.email,
        password: formData.password,
        loginAfterCreate: true,
        userClass: AdornisUser,
      });

      const normalizedSubjects = Array.isArray(formData.subjects)
        ? formData.subjects.filter(subject => typeof subject === 'string' && subject.trim().length > 0)
        : [];

      const candidateUserId = (registeredUser as any)?._id || username;
      const guestUserID = getGuestUserID();

      const createProfileMutation = formData.role === 'student' ? createStudentMutation : createTeacherMutation;

      await createProfileMutation(
        candidateUserId,
        formData.firstName,
        formData.lastName,
        formData.school,
        normalizedSubjects,
        formData.email,
        formData.password,
        guestUserID,
      )();

      logger.info(`User registered and ${formData.role} profile created`);
      await subscribeCurrentUser();
      router.goTo('home');
    } catch (error: any) {
      logger.error(error, `Error during sign up:`);
      setSignUpError(error?.message || 'Registration fehlgeschlagen. Bitte versuche es erneut.');
    } finally {
      setIsSubmitting(false);
    }
  };

  const handleChange = (e: React.ChangeEvent<HTMLInputElement | HTMLSelectElement>) => {
    const { name, value } = e.target;
    setFormData(prev => ({ ...prev, [name]: value }));
  };

  const handleSubjectToggle = (subject: Subject) => {
    setFormData(prev => {
      const nextSubjects = prev.subjects.includes(subject)
        ? prev.subjects.filter(s => s !== subject)
        : [...prev.subjects, subject];
      return { ...prev, subjects: nextSubjects };
    });
  };

  return (
    <div className="min-h-screen bg-white dark:bg-background flex relative">
      <div className="hidden lg:block absolute top-8 left-8 z-10">
        <h1 className="text-2xl font-['Futura'] font-semibold text-brand-500">gradias</h1>
      </div>
      <div className="hidden lg:block lg:w-1/4 min-h-screen relative overflow-hidden">
        <img
          src={OwlLogo}
          alt="Gradias Owl Logo"
          className="absolute top-1/2 left-0 -translate-x-1/2 -translate-y-1/2 w-[200%] h-auto max-w-none opacity-90"
        />
      </div>

      <div className="w-full lg:w-1/2 flex items-center justify-center p-8">
        <div className="w-full max-w-md">
          <h2 className="text-4xl font-bold text-slate-900 dark:text-slate-100 mb-8">Sign Up</h2>

          <form onSubmit={handleSubmit} className="space-y-3">
            <div>
              <label className="text-sm font-medium text-slate-600 dark:text-slate-400 mb-1.5 block">I am a</label>
              <div className="grid grid-cols-2 gap-2">
                {(['teacher', 'student'] as const).map(role => (
                  <button
                    key={role}
                    type="button"
                    onClick={() => setFormData(prev => ({ ...prev, role }))}
                    className={`px-3 py-2 rounded-lg text-sm font-medium border transition-colors capitalize ${
                      formData.role === role
                        ? 'bg-brand-500 text-white border-brand-500'
                        : 'bg-white dark:bg-card text-slate-700 dark:text-slate-300 border-slate-300 dark:border-slate-600 hover:bg-slate-50 dark:hover:bg-slate-800'
                    }`}
                  >
                    {role}
                  </button>
                ))}
              </div>
            </div>
            <div className="grid grid-cols-2 gap-4">
              <LabeledInput
                id="firstName"
                name="firstName"
                label="First Name"
                value={formData.firstName}
                onChange={handleChange}
                required={requiredFields.firstName}
                showValidation={showValidation}
              />
              <LabeledInput
                id="lastName"
                name="lastName"
                label="Last Name"
                value={formData.lastName}
                onChange={handleChange}
                required={requiredFields.lastName}
                showValidation={showValidation}
              />
            </div>

            <LabeledInput
              id="school"
              name="school"
              label="School"
              value={formData.school}
              onChange={handleChange}
              placeholder="Enter your school name"
              required={requiredFields.school}
              showValidation={showValidation}
            />

            {formData.role === 'teacher' && (
              <MultiSelectDropdown
                id="subjects"
                label="Subjects"
                options={ALL_SUBJECTS}
                selectedValues={formData.subjects}
                onToggle={handleSubjectToggle}
                required={requiredFields.subjects}
                showValidation={showValidation}
                placeholder="Select your subjects"
                formatOption={subject => t(`Subjects.${subject}`)}
              />
            )}

            <LabeledInput
              id="email"
              name="email"
              label="E-Mail"
              type="email"
              value={formData.email}
              onChange={handleChange}
              placeholder="Enter your email"
              required={requiredFields.email}
              showValidation={showValidation}
            />
            <LabeledInput
              id="password"
              name="password"
              label="Password"
              type="password"
              value={formData.password}
              onChange={handleChange}
              placeholder="Enter your password"
              required={requiredFields.password}
              showValidation={showValidation}
            />
            <LabeledInput
              id="confirmPassword"
              name="confirmPassword"
              label="Confirm Password"
              type="password"
              value={formData.confirmPassword}
              onChange={handleChange}
              placeholder="Confirm your password"
              required={requiredFields.confirmPassword}
              showValidation={showValidation}
            />

            {signUpError && (
              <div className="bg-red-50 border border-red-200 text-red-700 px-4 py-3 rounded-lg text-sm">
                {signUpError}
              </div>
            )}

            <button
              type="submit"
              disabled={isSubmitting}
              className="w-full bg-brand-500 text-white py-3 rounded-lg font-medium hover:bg-[brand-600] transition-colors mt-6 disabled:opacity-50 disabled:cursor-not-allowed"
            >
              {isSubmitting ? 'Creating account…' : 'Sign Up'}
            </button>
          </form>
        </div>
      </div>
      <div className="hidden lg:block lg:w-1/4 min-h-screen relative overflow-hidden">
        <div
          className="absolute bottom-0 right-0 w-[200%] aspect-square translate-x-1/2 translate-y-1/2 rounded-full animate-[spin_30s_linear_infinite]"
          style={{
            background:
              'radial-gradient(circle at 30% 30%, brand-500 0%, brand-200 35%, rgba(137, 180, 214, 0.1) 70%, transparent 100%)',
          }}
        />
      </div>
    </div>
  );
}
